import { useCallback, useEffect, useRef, useState } from "react";

type ActionStatus = "sending" | "sent" | "error";
type ActionStateMap = Record<number, ActionStatus>;

/**
 * Result shape the host's action callback returns. Mirrors
 * `ReplyResult` so the host can keep one wire-format response type
 * that both reply and action paths consume.
 */
export interface ActionResult {
  success: boolean;
  error?: string;
}

interface UseActionStatesArgs {
  /**
   * Host-supplied dispatch callback. Hook owns the per-action FSM
   * (sending → sent | error → idle) and calls this when a button
   * action is clicked. Extension wires to
   * `browser.runtime.sendMessage`; web wires to `fetch`.
   *
   * Receives the actionIndex (matches the wire-format
   * `NotificationAction.actionIndex`). Returns a Promise so the
   * hook can flip the per-action status based on the result.
   */
  dispatch: (actionIndex: number) => Promise<ActionResult>;
}

export interface ActionStates {
  states: ActionStateMap;
  dispatch: (actionIndex: number) => void;
}

/**
 * Per-action feedback state machine for a single notification. Owns
 * the `{ [actionIndex]: "sending" | "sent" | "error" }` map and the
 * auto-clear timers that return each action to idle after 2s / 3s.
 *
 * `dispatch` is stable across renders — handlers closed over via
 * `useCallback` with stable deps — so `() => dispatch(i)` closures
 * the consumer creates in its render body don't thrash downstream
 * memoized descendants that take the handler as a prop.
 *
 * Pending per-action timers are cancelled on unmount and before any
 * overwrite (rapid re-dispatch doesn't orphan a prior clear).
 */
export function useActionStates({ dispatch: hostDispatch }: UseActionStatesArgs): ActionStates {
  const [states, setStates] = useState<ActionStateMap>({});
  const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  // Mirror the host dispatcher so the public `dispatch` keeps a
  // stable identity even when the host's callback is freshly created
  // by a parent re-render. Same trick as useReplyState's stateRef.
  // The mirror lives in useEffect (not the render body) so it only
  // runs for committed renders — React 18 concurrent-safe (see #423).
  const dispatchRef = useRef(hostDispatch);
  useEffect(() => {
    dispatchRef.current = hostDispatch;
  });

  useEffect(
    () => () => {
      for (const timer of Object.values(timersRef.current)) clearTimeout(timer);
    },
    []
  );

  const scheduleClear = useCallback((actionIndex: number, delayMs: number) => {
    if (timersRef.current[actionIndex]) clearTimeout(timersRef.current[actionIndex]);
    timersRef.current[actionIndex] = setTimeout(() => {
      delete timersRef.current[actionIndex];
      setStates((prev) => {
        const next = { ...prev };
        delete next[actionIndex];
        return next;
      });
    }, delayMs);
  }, []);

  const dispatchAsync = useCallback(
    async (actionIndex: number) => {
      setStates((prev) => ({ ...prev, [actionIndex]: "sending" }));
      try {
        const result = await dispatchRef.current(actionIndex);
        if (result.success) {
          setStates((prev) => ({ ...prev, [actionIndex]: "sent" }));
          scheduleClear(actionIndex, 2000);
        } else {
          setStates((prev) => ({ ...prev, [actionIndex]: "error" }));
          scheduleClear(actionIndex, 3000);
        }
      } catch {
        setStates((prev) => ({ ...prev, [actionIndex]: "error" }));
        scheduleClear(actionIndex, 3000);
      }
    },
    [scheduleClear]
  );

  // Stable void wrapper — mirrors useReplyState.send so consumers get
  // a stable function identity they can pass to memoized descendants.
  const dispatch = useCallback(
    (actionIndex: number) => {
      void dispatchAsync(actionIndex);
    },
    [dispatchAsync]
  );

  return { states, dispatch };
}
